// 抖音直播经营聚合纯函数：逻辑逐一移植自
// designs/douyin-live-session-analysis/template_spu_sku.html 的 aggSpu/aggSku/场次过滤。
import type {
  DouyinCatalogSku,
  DouyinLiveAnalysis,
  DouyinSession,
  DouyinSkuDailyRow,
  DouyinSpuDailyRow,
} from './douyinTypes'

export interface DouyinDateRange {
  startDate: string
  endDate: string
}

const inRange = (date: string, range: DouyinDateRange) =>
  date >= range.startDate && date <= range.endDate

export interface DouyinSpuAggregate {
  pid: string
  name: string
  img: string | null
  inCatalog: boolean
  status: string | null
  active: boolean
  paid: number
  items: number
  orders: number
  refund: number
  nocost: number
  /** 无销售行时为 null（不显示 0 成本） */
  cost: number | null
  gm: number | null
  gmr: number | null
  /** 范围内动销 SKU 数 */
  skuN: number
  /** 商品库 SKU 变体数，未在商品库为 null */
  varN: number | null
}

/** SPU 聚合：基表＝商品库全量 SPU，无销售行的以 0 补全。 */
export function aggregateSpus(data: DouyinLiveAnalysis, range: DouyinDateRange): DouyinSpuAggregate[] {
  const totals = new Map<string, { paid: number; items: number; orders: number; refund: number; cost: number; nocost: number }>()
  for (const row of data.spuDaily) {
    if (!inRange(row.d, range)) continue
    let aggregate = totals.get(row.pid)
    if (!aggregate) totals.set(row.pid, aggregate = { paid: 0, items: 0, orders: 0, refund: 0, cost: 0, nocost: 0 })
    aggregate.paid += row.paid || 0
    aggregate.items += row.items || 0
    aggregate.orders += row.orders || 0
    aggregate.refund += row.refund || 0
    aggregate.cost += row.cost || 0
    aggregate.nocost += row.nocost || 0
  }
  const skuSets = new Map<string, Set<string>>()
  for (const row of data.skuDaily) {
    if (!inRange(row.d, range)) continue
    let names = skuSets.get(row.pid)
    if (!names) skuSets.set(row.pid, names = new Set())
    names.add(row.n)
  }
  const rows: DouyinSpuAggregate[] = []
  for (const pid of Object.keys(data.spuInfo)) {
    const info = data.spuInfo[pid]
    const sales = totals.get(pid)
    const active = Boolean(sales)
    const base = sales || { paid: 0, items: 0, orders: 0, refund: 0, cost: 0, nocost: 0 }
    rows.push({
      pid,
      name: info?.name || pid,
      img: info?.img || null,
      inCatalog: Boolean(info?.inCatalog),
      status: info?.status || null,
      active,
      paid: base.paid,
      items: base.items,
      orders: base.orders,
      refund: base.refund,
      nocost: active ? base.nocost : 0,
      cost: active ? base.cost : null,
      gm: active ? base.paid - base.cost : null,
      gmr: active && base.paid > 0 ? (base.paid - base.cost) / base.paid : null,
      skuN: skuSets.get(pid)?.size || 0,
      varN: info?.variantCount ?? null,
    })
  }
  return rows
}

export interface DouyinSkuAggregate {
  key: string
  vid: string | null
  pid: string
  n: string
  name: string
  img: string | null
  code: string | null
  uc: number | null
  company: string | null
  price: number | null
  stock: number | null
  dyVol: number | null
  active: boolean
  /** 当前商品库缺失的历史 SKU 或无 vid 的罗盘兜底行 */
  noCatalog: boolean
  paid: number
  items: number
  orders: number
  refund: number
  cost: number | null
  gm: number | null
  gmr: number | null
  linked: number
}

interface SkuSalesTotal { paid: number; items: number; orders: number; refund: number }

const finishSkuRow = (row: Omit<DouyinSkuAggregate, 'cost' | 'gm' | 'gmr' | 'linked'>): DouyinSkuAggregate => {
  const cost = row.active && row.uc != null ? row.uc * row.items : null
  const gm = row.active && cost != null ? row.paid - cost : null
  return {
    ...row,
    cost,
    gm,
    gmr: gm != null && row.paid > 0 ? gm / row.paid : null,
    linked: row.code ? 1 : 0,
  }
}

/** SKU 聚合：当前商品库全量变体，加历史销售中已不在库的 SKU；有 vid 时始终按 vid 合并。 */
export function aggregateSkus(data: DouyinLiveAnalysis, range: DouyinDateRange): DouyinSkuAggregate[] {
  const byVid = new Map<string, SkuSalesTotal>()
  const byKey = new Map<string, SkuSalesTotal>()
  const catalogVids = new Set(data.catalogSkus.map((row) => row.vid))
  const historicalIds = new Map<string, { vid: string | null; pid: string; n: string }>()
  for (const row of data.skuDaily) {
    const key = `${row.pid}|${row.n}`
    const identity = row.vid || key
    if ((!row.vid || !catalogVids.has(row.vid)) && !historicalIds.has(identity)) {
      historicalIds.set(identity, { vid: row.vid, pid: row.pid, n: row.n })
    }
    if (!inRange(row.d, range)) continue
    const map = row.vid ? byVid : byKey
    const mapKey = row.vid ? row.vid : key
    let aggregate = map.get(mapKey)
    if (!aggregate) map.set(mapKey, aggregate = { paid: 0, items: 0, orders: 0, refund: 0 })
    aggregate.paid += row.paid || 0
    aggregate.items += row.items || 0
    aggregate.orders += row.orders || 0
    aggregate.refund += row.refund || 0
  }
  const rows: DouyinSkuAggregate[] = []
  for (const catalogSku of data.catalogSkus) {
    const sales = byVid.get(catalogSku.vid)
    rows.push(finishSkuRow({
      key: catalogSku.vid,
      vid: catalogSku.vid,
      pid: catalogSku.pid,
      n: catalogSku.n,
      name: data.spuInfo[catalogSku.pid]?.name || catalogSku.pid,
      img: catalogSku.img || null,
      code: catalogSku.code || null,
      uc: catalogSku.uc ?? null,
      company: catalogSku.company || null,
      price: catalogSku.price ?? null,
      stock: catalogSku.stock ?? null,
      dyVol: catalogSku.dyVol ?? null,
      paid: sales?.paid || 0,
      items: sales?.items || 0,
      orders: sales?.orders || 0,
      refund: sales?.refund || 0,
      active: Boolean(sales),
      noCatalog: false,
    }))
  }
  for (const [key, id] of historicalIds) {
    const sales = id.vid ? byVid.get(id.vid) : byKey.get(key)
    const info = data.skuInfo[`${id.pid}|${id.n}`]
    rows.push(finishSkuRow({
      key,
      vid: id.vid,
      pid: id.pid,
      n: id.n,
      name: data.spuInfo[id.pid]?.name || id.pid,
      img: info?.img || null,
      code: info?.code || null,
      uc: info?.uc ?? null,
      company: info?.company || null,
      price: null,
      stock: null,
      dyVol: null,
      paid: sales?.paid || 0,
      items: sales?.items || 0,
      orders: sales?.orders || 0,
      refund: sales?.refund || 0,
      active: Boolean(sales),
      noCatalog: true,
    }))
  }
  return rows
}

export type DouyinSessionTypeFilter = 'all' | 'self' | 'partner'

/** 场次范围过滤：type=self 为非达人合作场。 */
export function filterSessions(
  data: DouyinLiveAnalysis,
  range: DouyinDateRange,
  type: DouyinSessionTypeFilter = 'all',
): DouyinSession[] {
  return data.sessions.filter((session) => {
    if (!inRange(session.date, range)) return false
    if (type === 'all') return true
    return (type === 'partner') === (session.type === 'partner')
  })
}

/** 按开播日分组（日期降序，组内按开播时刻升序）。 */
export function groupSessionsByDate(sessions: DouyinSession[]): Array<{ date: string; sessions: DouyinSession[] }> {
  const byDate = new Map<string, DouyinSession[]>()
  for (const session of sessions) {
    const group = byDate.get(session.date)
    if (group) group.push(session)
    else byDate.set(session.date, [session])
  }
  return [...byDate.entries()]
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([date, group]) => ({
      date,
      sessions: group.slice().sort((left, right) => (left.start < right.start ? -1 : 1)),
    }))
}

export const douyinMedian = (values: number[]): number | null => {
  if (!values.length) return null
  const sorted = [...values].sort((left, right) => left - right)
  const middle = sorted.length >> 1
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

export const douyinWeekday = (date: string) => '日一二三四五六'[new Date(`${date}T00:00:00`).getDay()]

export interface DouyinSpuDetailSkuRow {
  n: string
  paid: number
  items: number
  orders: number
  refund: number
  img: string | null
  code: string | null
  uc: number | null
  cost: number | null
  gm: number | null
  gmr: number | null
  linked: number
}

/** SPU 详情页「旗下 SKU 销售明细」：范围内按规格名聚合，档案取 skuInfo。 */
export function aggregateSpuDetailSkus(
  data: DouyinLiveAnalysis,
  pid: string,
  range: DouyinDateRange,
): DouyinSpuDetailSkuRow[] {
  const totals = new Map<string, { n: string; paid: number; items: number; orders: number; refund: number }>()
  for (const row of data.skuDaily) {
    if (row.pid !== pid || !inRange(row.d, range)) continue
    let aggregate = totals.get(row.n)
    if (!aggregate) totals.set(row.n, aggregate = { n: row.n, paid: 0, items: 0, orders: 0, refund: 0 })
    aggregate.paid += row.paid || 0
    aggregate.items += row.items || 0
    aggregate.orders += row.orders || 0
    aggregate.refund += row.refund || 0
  }
  return [...totals.values()].map((aggregate) => {
    const info = data.skuInfo[`${pid}|${aggregate.n}`]
    const uc = info?.uc ?? null
    const cost = uc != null ? uc * aggregate.items : null
    const gm = cost != null ? aggregate.paid - cost : null
    return {
      ...aggregate,
      img: info?.img || null,
      code: info?.code || null,
      uc,
      cost,
      gm,
      gmr: gm != null && aggregate.paid > 0 ? gm / aggregate.paid : null,
      linked: info?.code ? 1 : 0,
    }
  })
}

/** 该 SPU 全部每日行（升序），趋势与每日明细共用，不随统计范围过滤。 */
export function spuDailyRowsAsc(data: DouyinLiveAnalysis, pid: string): DouyinSpuDailyRow[] {
  return data.spuDaily
    .filter((row) => row.pid === pid)
    .slice()
    .sort((left, right) => left.d.localeCompare(right.d))
}

/** 该 SPU 全部动销 SKU 规格数（全窗口，不随统计范围过滤，身份卡用）。 */
export function spuAllTimeSkuCount(data: DouyinLiveAnalysis, pid: string): number {
  return new Set(data.skuDaily.filter((row) => row.pid === pid).map((row) => row.n)).size
}

/** 该 SPU 在商品库的全部 SKU 变体（详情页零销量兜底用）。 */
export function catalogSkusOf(data: DouyinLiveAnalysis, pid: string): DouyinCatalogSku[] {
  return data.catalogSkus.filter((catalogSku) => catalogSku.pid === pid)
}

/** 通用排序：数字列 null 沉底，并列按名称稳定排序（照设计稿 sortRows）。 */
export function sortDouyinRows<T>(
  rows: T[],
  sort: { key: string; desc: boolean },
  nameOf: (row: T) => string,
): T[] {
  return rows.slice().sort((left, right) => {
    const leftValue = (left as Record<string, unknown>)[sort.key]
    const rightValue = (right as Record<string, unknown>)[sort.key]
    if (typeof leftValue === 'string' || typeof rightValue === 'string') {
      const leftText = String(leftValue ?? '')
      const rightText = String(rightValue ?? '')
      return sort.desc ? rightText.localeCompare(leftText, 'zh-CN') : leftText.localeCompare(rightText, 'zh-CN')
    }
    const leftNumber = leftValue == null ? -Infinity : Number(leftValue)
    const rightNumber = rightValue == null ? -Infinity : Number(rightValue)
    const delta = sort.desc ? rightNumber - leftNumber : leftNumber - rightNumber
    if (delta) return delta
    return nameOf(left).localeCompare(nameOf(right), 'zh-CN')
  })
}

export const douyinSkuDailyRowKey = (row: DouyinSkuDailyRow) => `${row.pid}|${row.n}`
